import { navBarActionButtonStyles } from "layout/nav-bar/nav-bar.styles";
import { FC, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  ActionButton,
  ContextualMenuItemType,
  DirectionalHint,
  IButtonStyles,
  IContextualMenuItem,
  mergeStyleSets,
  useTheme
} from "@bps/fluent-ui";

import {
  ComponentCode,
  ComponentStatus,
  TenantFeaturesCode
} from "../../core/gateways/plt-catalog/plt-catalog.dtos";
import {
  usePltcCatalogComponent,
  useTenantFeatureChecker
} from "../../core/gateways/plt-catalog/plt-catalog.hooks";
import { LicenseDialog } from "./LicenseDialog";

interface HelpButtonProps {
  styles?: IButtonStyles;
}

type Keys =
  | "PracticeProfile"
  | "OnlineSettings"
  | "AppointmentTypes"
  | "Providers"
  | "ExclusionPeriods";

type AppCue = {
  [key in Keys]: { key: string; path: string };
};

const OldAppCues: AppCue = {
  PracticeProfile: {
    key: "1c78ca6c-1df4-45f4-ad0c-9a55c8263c02",
    path: "/booking/practice-profile"
  },
  OnlineSettings: {
    key: "650d67cf-fa05-48a2-9e4e-4a4514dcec62",
    path: "/booking/online-settings"
  },
  AppointmentTypes: {
    key: "d5372743-3c00-40cf-aa80-d4c35b48e5c6",
    path: "/booking/appointment-types"
  },
  Providers: {
    key: "4c7553f1-2d61-418e-b500-00969332aadb",
    path: "/booking/providers"
  },
  ExclusionPeriods: {
    key: "b645abf9-204c-4d03-bcad-6741509e04c4",
    path: "/booking/exclusion-periods"
  }
};

const AppCues: AppCue = {
  PracticeProfile: {
    key: "1d5731d1-6758-4f35-bf08-3a0b5dde3e5d",
    path: "bhb/practice-profile"
  },
  OnlineSettings: {
    key: "56ebb3a7-70f9-4625-827c-3ad64e0b1c7f",
    path: "bhb/online-settings"
  },
  AppointmentTypes: {
    key: "8c6a5533-59f5-41ad-ac5b-0c5921237a34",
    path: "bhb/appointment-types"
  },
  Providers: {
    key: "922d6b9a-5b05-446b-be23-bce60cc68076",
    path: "bhb/providers"
  },
  ExclusionPeriods: {
    key: "b317e39b-b4bb-4fc5-abde-171df037773b",
    path: "bhb/exclusion-periods"
  }
};

export const Help: FC<HelpButtonProps> = ({ styles }) => {
  const theme = useTheme();
  const currentLocation = useLocation();
  const navigate = useNavigate();

  const [showLicenseModal, setShowLicenseModal] = useState<boolean>(false);
  const isBhbEnabled = useTenantFeatureChecker(
    TenantFeaturesCode.prosBhbIntegration
  );

  const { data: bpmIntegrationComponent } = usePltcCatalogComponent(
    ComponentCode.bpmIntegration
  );

  const menuItemSelected = (
    ev?: React.MouseEvent<HTMLButtonElement>,
    item?: IContextualMenuItem
  ) => {
    if (item?.data === currentLocation.pathname) {
      // Appcues is loaded by Google Tag Manager, it does not appear on the Window, therefore using a @ts-ignore tag here.
      // @ts-ignore
      Appcues.show(item.key);
    } else {
      navigate(`${item?.data}?appcue=${item?.key}`);
    }
  };

  const divider: IContextualMenuItem = {
    key: "divider",
    itemType: ContextualMenuItemType.Divider
  };

  const isSettingsRoute = location.pathname.startsWith("/settings/");
  const isBookingRoute = location.pathname.startsWith("/booking/");
  const {
    PracticeProfile,
    OnlineSettings,
    Providers,
    AppointmentTypes,
    ExclusionPeriods
  } = isBookingRoute ? OldAppCues : AppCues;

  const appCuesItems: IContextualMenuItem[] = [
    {
      key: PracticeProfile.key,
      data: PracticeProfile.path,
      text: "Practice Profile",
      onClick: menuItemSelected
    },
    {
      key: OnlineSettings.key,
      data: OnlineSettings.path,
      text: "Online Settings",
      onClick: menuItemSelected
    },
    {
      key: AppointmentTypes.key,
      data: AppointmentTypes.path,
      text: "Appointment Types",
      onClick: menuItemSelected
    },
    {
      key: Providers.key,
      data: Providers.path,
      text: "Providers",
      onClick: menuItemSelected
    },
    {
      key: ExclusionPeriods.key,
      data: ExclusionPeriods.path,
      text: "Exclusion Periods",
      onClick: menuItemSelected
    }
  ];

  const bhbSubMenuItems: IContextualMenuItem[] = [
    {
      key: "interactiveGuide",
      text: "Interactive guide",
      subMenuProps: {
        items: appCuesItems,
        calloutProps: {
          calloutMaxWidth: 150
        },
        directionalHint: DirectionalHint.leftTopEdge
      }
    },
    {
      key: "knowledgeBase",
      text: "Knowledge base",
      onClick: () => {
        window.open(
          "https://kb.bestpracticesoftware.com/bh/bookings/index.htm",
          "_blank"
        );
      }
    },
    {
      key: "releaseNotes",
      text: "Release notes",
      onClick: () => {
        window.open(
          "https://kb.bestpracticesoftware.com/bh/bookings/about/whatsnew.htm",
          "_blank"
        );
      }
    }
  ];

  const bpmSubMenuItems: IContextualMenuItem[] = [
    {
      key: "ideasPortal",
      text: "Ideas portal",
      onClick: () => {
        window.open("https://bestpracticemobile.ideas.aha.io/", "_blank");
      }
    },
    {
      key: "knowledgeBase",
      text: "Knowledge base",
      onClick: () => {
        window.open(
          "https://kb.bpsoftware.net/bestpracticemobile/index.htm",
          "_blank"
        );
      }
    },
    {
      key: "releaseNotes",
      text: "Release notes",
      onClick: () => {
        window.open(
          "https://kb.bpsoftware.net/bestpracticemobile/whatsnew/whatsnew.htm",
          "_blank"
        );
      }
    },
    {
      key: "thirdPartyLicences.",
      text: "Third-party licences",
      onClick: () => {
        setShowLicenseModal(true);
      }
    }
  ];

  const items: IContextualMenuItem[] = [
    ...(bpmIntegrationComponent &&
    (bpmIntegrationComponent.status === ComponentStatus.Enabled ||
      bpmIntegrationComponent.status === ComponentStatus.Disabled)
      ? [
          {
            key: "bestPracticeMobile",
            itemType: ContextualMenuItemType.Section,
            sectionProps: {
              title: "Best Practice Mobile",
              items: bpmSubMenuItems
            }
          }
        ]
      : []),
    // Only add Best Health Booking when tenant has BHB enabled and under /settings/* or /booking/* routes
    ...(isBhbEnabled && (isSettingsRoute || isBookingRoute)
      ? [
          divider,
          {
            key: "bestHealthBooking",
            itemType: ContextualMenuItemType.Section,
            sectionProps: {
              title: "Best Health Booking",
              items: bhbSubMenuItems
            }
          } as IContextualMenuItem
        ]
      : [])
  ];

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <ActionButton
        text="Help"
        styles={mergeStyleSets(
          navBarActionButtonStyles(theme, false, true),
          styles
        )}
        menuProps={{
          shouldFocusOnMount: true,
          items,
          directionalHint: DirectionalHint.bottomRightEdge
        }}
      />
      <LicenseDialog
        showModal={showLicenseModal}
        onDismiss={() => {
          setShowLicenseModal(false);
        }}
      />
    </>
  );
};
